// Design agent: Spec 014 — everything MasqIP knows about an inspected address today comes from
// static tables baked into the app (data/ipAddressCatalog.js). For a *public* address, a lot more is
// knowable from third parties (whois/RDAP, ASN/BGP, reverse DNS, geolocation) — but only if the user
// explicitly asks for it, since fetching any of that necessarily sends the address to those third
// parties. This file owns both pieces of UI spec 014 describes: the per-card "External lookups"
// section (`ExternalIntel`) and the inspector-header "My public address" + "Connection debug" panel
// (`PublicAddressPanel`). They share one opt-in gate (`useExternalLookupsOptIn`) so enabling lookups
// once, anywhere, unlocks both.
// Developer agent: All network calls are delegated to src/utils/externalLookups.js, which already
// guarantees every fetch degrades to `{ ok: false }` instead of throwing — this file only has to
// decide what to render for each state (loading / ok / not ok), never how to catch a network error.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  readExternalLookupsOptIn,
  writeExternalLookupsOptIn,
  isPublicAddressCategory,
  fetchRdap,
  fetchIpapiIs,
  fetchReverseDns,
  fetchPublicIp,
  fetchCloudflareTrace,
  buildExternalLinks,
  getCachedExternalLookup,
  setCachedExternalLookup,
} from '../utils/externalLookups';
import './ExternalIntel.css';

// Design agent: localStorage's own 'storage' event only fires in *other* browser tabs/windows, never
// in the tab that made the change — but this app can have several ExternalIntel/PublicAddressPanel
// instances mounted at once (one per public-address insight card, plus the header panel), and
// clicking "Enable" in any one of them must immediately unlock every other one on the same page.
// Developer agent: A plain custom DOM event, dispatched right after the localStorage write, keeps
// every mounted instance's React state in sync without needing a shared state library.
const OPT_IN_CHANGE_EVENT = 'masqip:external-lookups-optin-changed';

// Design agent: One small hook shared by both exported components so the opt-in read/write/sync
// logic exists exactly once.
function useExternalLookupsOptIn() {
  const [isOptedIn, setIsOptedIn] = useState(() => readExternalLookupsOptIn());

  useEffect(() => {
    const handleChange = () => setIsOptedIn(readExternalLookupsOptIn());
    window.addEventListener(OPT_IN_CHANGE_EVENT, handleChange);
    window.addEventListener('storage', handleChange);
    return () => {
      window.removeEventListener(OPT_IN_CHANGE_EVENT, handleChange);
      window.removeEventListener('storage', handleChange);
    };
  }, []);

  const enableOptIn = useCallback(() => {
    writeExternalLookupsOptIn(true);
    setIsOptedIn(true);
    window.dispatchEvent(new Event(OPT_IN_CHANGE_EVENT));
  }, []);

  return [isOptedIn, enableOptIn];
}

// Design agent: The one-line consent notice spec 014 item 1 requires, shared verbatim by both the
// per-card section and the header panel so the promise made is identical everywhere.
function ExternalLookupsConsentNotice({ idPrefix, onEnable }) {
  return (
    <div className="external-intel-consent" id={`${idPrefix}-consent`}>
      <p className="external-intel-consent-text" id={`${idPrefix}-consent-text`}>
        Enabling this sends the address to rdap.org, ipapi.is, and Cloudflare&apos;s DNS-over-HTTPS
        resolver so they can look it up. Nothing is sent until you enable it, and the choice is
        remembered on this device.
      </p>
      <button
        type="button"
        className="ghost-button external-intel-enable-button"
        id={`${idPrefix}-enable-button`}
        onClick={onEnable}
      >
        Enable external lookups
      </button>
    </div>
  );
}

// Design agent: One fetch-backed row (RDAP / ipapi.is / reverse DNS) with its own loading/ok/error
// presentation, so a slow or failing source never blocks the other two from showing their result.
function ExternalIntelSourceRow({ idPrefix, label, lookup, renderData, fallbackUrl }) {
  return (
    <div className="external-intel-row" id={idPrefix}>
      <p className="external-intel-row-label" id={`${idPrefix}-label`}>
        {label}
      </p>
      {!lookup && (
        <p className="external-intel-row-loading" id={`${idPrefix}-loading`}>
          Looking up…
        </p>
      )}
      {lookup && lookup.ok && (
        <div className="external-intel-row-data" id={`${idPrefix}-data`}>
          {renderData(lookup.data)}
        </div>
      )}
      {lookup && !lookup.ok && (
        <p className="external-intel-row-unavailable" id={`${idPrefix}-unavailable`}>
          Unavailable ({lookup.error || 'request failed'}).{' '}
          {fallbackUrl && (
            <a href={fallbackUrl} target="_blank" rel="noreferrer noopener" id={`${idPrefix}-fallback-link`}>
              View source directly
            </a>
          )}
        </p>
      )}
    </div>
  );
}

// Design agent: Spec 014 item 1/2 — the collapsed "External lookups" section mounted inside
// InspectorCard for every public (non-RFC1918/special-use) address. Nothing is fetched until the
// section is both opted in globally *and* expanded by the user on this particular card.
// Developer agent: `ip` is the dotted address string being inspected (InspectorCard passes
// `analysis.inspectedIp`). Results are cached per address (src/utils/externalLookups.js) so
// collapsing and re-expanding the same card, or inspecting the same address twice, never refetches.
function ExternalIntel({ ip, idPrefix }) {
  const [isOptedIn, enableOptIn] = useExternalLookupsOptIn();
  const [isExpanded, setIsExpanded] = useState(false);
  const [lookups, setLookups] = useState(() => getCachedExternalLookup(ip));

  useEffect(() => {
    if (!isOptedIn || !isExpanded) {
      return undefined;
    }
    const cached = getCachedExternalLookup(ip);
    if (cached) {
      setLookups(cached);
      return undefined;
    }
    let isCancelled = false;
    setLookups(null);
    (async () => {
      const [rdap, ipapi, reverseDns] = await Promise.all([
        fetchRdap(ip),
        fetchIpapiIs(ip),
        fetchReverseDns(ip),
      ]);
      if (isCancelled) {
        return;
      }
      const results = { rdap, ipapi, reverseDns };
      setLookups(results);
      setCachedExternalLookup(ip, results);
    })();
    return () => {
      isCancelled = true;
    };
  }, [ip, isOptedIn, isExpanded]);

  const links = buildExternalLinks(ip);

  return (
    <div className="external-intel" id={idPrefix}>
      <button
        type="button"
        className="external-intel-toggle"
        id={`${idPrefix}-toggle`}
        onClick={() => setIsExpanded((previous) => !previous)}
        aria-expanded={isExpanded}
      >
        External lookups {isExpanded ? '▲' : '▼'}
      </button>
      {isExpanded && (
        <div className="external-intel-body" id={`${idPrefix}-body`}>
          {!isOptedIn && <ExternalLookupsConsentNotice idPrefix={idPrefix} onEnable={enableOptIn} />}
          {isOptedIn && (
            <>
              <ExternalIntelSourceRow
                idPrefix={`${idPrefix}-rdap`}
                label="RDAP (whois)"
                lookup={lookups && lookups.rdap}
                fallbackUrl={`https://rdap.org/ip/${ip}`}
                renderData={(data) => (
                  <>
                    <span>{data.name} ({data.handle})</span>
                    <span>{data.range} · {data.country}</span>
                    <span>Registrar/RIR: {data.registrar}</span>
                  </>
                )}
              />
              <ExternalIntelSourceRow
                idPrefix={`${idPrefix}-ipapi`}
                label="ipapi.is"
                lookup={lookups && lookups.ipapi}
                fallbackUrl={`https://api.ipapi.is/?q=${ip}`}
                renderData={(data) => (
                  <>
                    <span>AS{data.asn} · {data.org}</span>
                    <span>{data.city}, {data.country}</span>
                    <span>
                      {data.isDatacenter && 'Datacenter '}
                      {data.isVpn && 'VPN '}
                      {data.isProxy && 'Proxy '}
                      {data.isAbuser && 'Reported abuser '}
                      {!data.isDatacenter && !data.isVpn && !data.isProxy && !data.isAbuser && 'No flags'}
                    </span>
                  </>
                )}
              />
              <ExternalIntelSourceRow
                idPrefix={`${idPrefix}-reverse-dns`}
                label="Reverse DNS (Cloudflare DoH)"
                lookup={lookups && lookups.reverseDns}
                fallbackUrl={`https://cloudflare-dns.com/dns-query?name=${ip}&type=PTR`}
                renderData={(data) => <span>{data.ptr || 'No PTR record'}</span>}
              />
              <div className="external-intel-links" id={`${idPrefix}-links`}>
                <a href={links.bgpHe} target="_blank" rel="noreferrer noopener" id={`${idPrefix}-link-bgp`}>
                  bgp.he.net
                </a>
                <a href={links.ipinfo} target="_blank" rel="noreferrer noopener" id={`${idPrefix}-link-ipinfo`}>
                  ipinfo.io
                </a>
                <a href={links.shodan} target="_blank" rel="noreferrer noopener" id={`${idPrefix}-link-shodan`}>
                  shodan.io
                </a>
                <a href={links.radar} target="_blank" rel="noreferrer noopener" id={`${idPrefix}-link-radar`}>
                  radar.cloudflare.com
                </a>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Design agent: Spec 014 item 3 — the "My public address" button + "Connection debug" block mounted
// once in the inspector's header (not per card, since there is only one visitor address to look up).
// Gated by the same opt-in as ExternalIntel; opening the panel before opting in shows the same
// consent notice instead of fetching anything.
// Developer agent: `onInspectAddress` lets the parent (IpInspector) fill the discovered address into
// a new entry field — this component has no opinion on how that entry list works, it just reports
// the address string back up.
function PublicAddressPanel({ onInspectAddress, idPrefix = 'ipv4-public-address-panel' }) {
  const [isOptedIn, enableOptIn] = useExternalLookupsOptIn();
  const [isOpen, setIsOpen] = useState(false);
  const [publicIpState, setPublicIpState] = useState('idle'); // idle | loading | ready | error
  const [publicIp, setPublicIp] = useState(null);
  // Design agent: Bug fix (owner report — panel froze on "Looking up your public address…" even
  // though ipify answered 200 with valid CORS headers). The error state used to be a plain string
  // ("Unable to reach ipify.org right now.") which named no service and offered no way out; it now
  // carries which request failed and why, plus the exact URL a link fallback can open, matching the
  // "Unavailable (<error>). View source directly" pattern already used by ExternalIntelSourceRow.
  const [publicIpError, setPublicIpError] = useState(null); // { message, sourceUrl } | null
  const [connectionDebug, setConnectionDebug] = useState(null);

  // Developer agent: Bug fix — this used to be a plain boolean derived from `publicIpState` itself
  // (`publicIpState !== 'idle'`), and that same `publicIpState` was also a dependency of the effect
  // below. Calling `setPublicIpState('loading')` from inside the effect therefore changed one of the
  // effect's own dependencies, which made React tear the effect down (running its cleanup, which
  // flips `isCancelled` to true for the *still in-flight* fetch) and immediately re-run it — so the
  // ipify response that arrived a moment later was always discarded as "cancelled", leaving the panel
  // stuck on "Looking up your public address…" forever with no error and nothing in the console,
  // exactly as reported. A ref survives across renders without ever being a reactive dependency, so
  // mutating it here cannot retrigger the effect — the fetch now runs exactly once per time the panel
  // is opened while opted in, and its result is always applied.
  const hasStartedPublicIpFetchRef = useRef(false);

  useEffect(() => {
    if (!isOpen || !isOptedIn || hasStartedPublicIpFetchRef.current) {
      return undefined;
    }
    hasStartedPublicIpFetchRef.current = true;
    let isCancelled = false;
    setPublicIpState('loading');
    (async () => {
      const ipResult = await fetchPublicIp();
      if (isCancelled) {
        return;
      }
      if (!ipResult.ok) {
        // Design agent: Spec's contract is "nothing reads as an error unless ipify itself failed,
        // and then the message says which service failed and offers a link fallback" — so the
        // error state carries the concrete reason (timeout / HTTP status / network error) and the
        // exact ipify URL a manual retry can open.
        setPublicIpError({ message: ipResult.error || 'request failed', sourceUrl: ipResult.sourceUrl });
        setPublicIpState('error');
        return;
      }
      // Design agent: The public IP is shown the moment ipify answers — the Connection debug block
      // below is a separate, slower step that must never delay or hide this result.
      setPublicIp(ipResult.data.ip);
      setPublicIpState('ready');

      // Design agent: Spec 014 item 3 — the Connection debug rows (AS name/number, Cloudflare
      // colo/WARP, DoH availability) are fetched right after the public address itself, since two of
      // the three rows need that address. Each of fetchIpapiIs/fetchCloudflareTrace/fetchReverseDns
      // already resolves to `{ ok: false, error }` instead of throwing (src/utils/externalLookups.js),
      // so Promise.all here can never reject — a slow or CORS-blocked source (e.g.
      // one.one.one.one/cdn-cgi/trace, which serves no Access-Control-Allow-Origin header) only ever
      // degrades its own row to "unavailable" below, never the whole block.
      const [ipapi, trace, reverseDns] = await Promise.all([
        fetchIpapiIs(ipResult.data.ip),
        fetchCloudflareTrace(),
        fetchReverseDns(ipResult.data.ip),
      ]);
      if (isCancelled) {
        return;
      }
      setConnectionDebug({ ipapi, trace, reverseDns });
    })();
    return () => {
      isCancelled = true;
    };
  }, [isOpen, isOptedIn]);

  const handleTogglePanel = () => {
    setIsOpen((previous) => !previous);
  };

  const handleInspectClick = () => {
    if (typeof onInspectAddress === 'function' && publicIp) {
      onInspectAddress(publicIp);
    }
  };

  // Design agent: Renders one Connection debug row with a graceful "unavailable" fallback, matching
  // the https://one.one.one.one/help/ style of a short label plus a Yes/No or value answer.
  const renderDebugRow = (rowId, label, value) => (
    <div className="connection-debug-row" id={rowId}>
      <span className="connection-debug-row-label" id={`${rowId}-label`}>{label}</span>
      <span className="connection-debug-row-value" id={`${rowId}-value`}>{value}</span>
    </div>
  );

  return (
    <div className="public-address-panel" id={idPrefix}>
      <button
        type="button"
        className="ghost-button public-address-toggle"
        id={`${idPrefix}-toggle`}
        onClick={handleTogglePanel}
        aria-expanded={isOpen}
      >
        My public address
      </button>
      {isOpen && (
        <div className="public-address-body" id={`${idPrefix}-body`}>
          {!isOptedIn && <ExternalLookupsConsentNotice idPrefix={idPrefix} onEnable={enableOptIn} />}
          {isOptedIn && (
            <>
              {publicIpState === 'loading' && (
                <p className="public-address-status" id={`${idPrefix}-loading`}>
                  Looking up your public address…
                </p>
              )}
              {publicIpState === 'error' && (
                <p className="public-address-status" id={`${idPrefix}-error`}>
                  Unable to reach ipify.org ({(publicIpError && publicIpError.message) || 'request failed'}).{' '}
                  {publicIpError && publicIpError.sourceUrl && (
                    <a
                      href={publicIpError.sourceUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      id={`${idPrefix}-error-fallback-link`}
                    >
                      View source directly
                    </a>
                  )}
                </p>
              )}
              {publicIpState === 'ready' && (
                <div className="public-address-result" id={`${idPrefix}-result`}>
                  <span className="public-address-value" id={`${idPrefix}-value`}>{publicIp}</span>
                  <button
                    type="button"
                    className="ghost-button public-address-inspect-button"
                    id={`${idPrefix}-inspect-button`}
                    onClick={handleInspectClick}
                  >
                    Inspect it
                  </button>
                </div>
              )}
              {connectionDebug && (
                <div className="connection-debug" id={`${idPrefix}-connection-debug`}>
                  <p className="connection-debug-title" id={`${idPrefix}-connection-debug-title`}>
                    Connection debug
                  </p>
                  {renderDebugRow(
                    `${idPrefix}-debug-asn`,
                    'AS name / number',
                    connectionDebug.ipapi.ok
                      ? `${connectionDebug.ipapi.data.org} (AS${connectionDebug.ipapi.data.asn})`
                      : 'unavailable'
                  )}
                  {renderDebugRow(
                    `${idPrefix}-debug-colo`,
                    'Cloudflare data centre',
                    connectionDebug.trace.ok ? (connectionDebug.trace.data.colo || 'unavailable') : 'unavailable'
                  )}
                  {renderDebugRow(
                    `${idPrefix}-debug-warp`,
                    'WARP',
                    connectionDebug.trace.ok ? (connectionDebug.trace.data.warp ? 'Yes' : 'No') : 'unavailable'
                  )}
                  {renderDebugRow(
                    `${idPrefix}-debug-doh`,
                    'DNS over HTTPS to 1.1.1.1',
                    connectionDebug.reverseDns.ok ? 'Yes' : 'unavailable'
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export { ExternalIntel, PublicAddressPanel, isPublicAddressCategory };
export default ExternalIntel;
